import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CardHeader,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { getContractItem, patchInvoiceItem, getInvoicesByContractId } from "../API/billingAPI";
import { getClient } from "../API/clientAPI";
import BaseWrapper from "../../components/shared/BaseWrapper";
import invoiceValidationSchema from "../../components/Billing/invoiceValidationSchema";
import { number } from "zod";
import { dateOnlyToDate, formatDateInPhilippines, todayInPhilippines } from "./utils/phDate";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

const formatDate = formatDateInPhilippines;

const normalizeDateOnly = (value) => {
  const dateString = formatDateInPhilippines(value);
  return dateString ? dateOnlyToDate(dateString) : null;
};

const getUnpaidInvoiceStatus = (dueDate) => {
  const due = normalizeDateOnly(dueDate);
  if (!due) return "OPEN";

  const today = normalizeDateOnly(todayInPhilippines());
  return today > due ? "OVERDUE" : "OPEN";
};

function RichTextDescriptionEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  return (
    <Box>
      <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
        <Button
          size="small"
          variant={editor.isActive("bold") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </Button>

        <Button
          size="small"
          variant={editor.isActive("italic") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </Button>

        <Button
          size="small"
          variant={editor.isActive("underline") ? "contained" : "outlined"}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          U
        </Button>
      </Box>

      <Box
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 1,
          minHeight: 90,
          "& .ProseMirror": {
            outline: "none",
            minHeight: 70,
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}


function EditInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const passedInvoice = location.state?.invoice;
  const selectedClient = location.state?.selectedClient;
  const selectedContract = location.state?.selectedContract;
  const contractSource = location.state?.contractSource;
  const source = location.state?.source || 'manageContracts';
  const returnTo = location.state?.returnTo;
  const contractId = selectedContract?.contract_id || selectedContract?.id;
  const CURRENCY = 'PHP';
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [clientLoading, setClientLoading] = useState(false);
  const [invoiceCountData, setInvoiceCountData] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [overrideWarnings, setOverrideWarnings] = useState([]);
  const currentUserRoles = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      const user = stored ? JSON.parse(stored) : null;
      return Array.isArray(user?.roles) ? user.roles : [];
    } catch {
      return [];
    }
  }, []);
  const isSystemAdmin = currentUserRoles.some((role) =>
    String(role || '').toLowerCase().replace(/\s+/g, '_') === 'system_admin'
  );

  const backRoute =
    source === 'manageClients' ? '/manage-clients' :
    source === 'viewContract' ? `/view-contract/${contractId}` :
    '/manage-contracts';

  const backState = useMemo(() => 
    source === 'viewContract'
    ? { contract: selectedContract, selectedClient, source: contractSource }
    : { selectedClient },
    [source, selectedContract, selectedClient, contractSource]
  );

  const getInvoiceFromPatchResult = (result) =>
    result?.invoice || result?.item || result?.data?.invoice || result?.data?.item || result?.data || result;

  const navigateAfterUpdate = (result, submitData) => {
    if (returnTo === 'viewInvoice') {
      const updatedInvoice = {
        ...passedInvoice,
        ...submitData,
        ...getInvoiceFromPatchResult(result),
      };

      navigate('/view-invoice', {
        state: {
          invoice: updatedInvoice,
          selectedClient,
          selectedContract,
          source,
          contractSource,
        },
      });
      return;
    }

    navigate(backRoute, { state: backState });
  };


  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    return dateOnlyToDate(dateStr);
  }

  const createInitialInvoice = () => ({
    invoice_number: passedInvoice?.invoice_number || '',
    contract_id: passedInvoice?.contract_id || '',
    client: {
      id: passedInvoice?.client?.id || selectedClient?.id || "",
      name: passedInvoice?.client?.name || selectedClient?.name || "",
      billing_address:
        passedInvoice?.client?.billing_address ||
        selectedClient?.billing_address ||
        "",
    },
    billing_date: parseDate(passedInvoice?.billing_date),
    due_date: parseDate(passedInvoice?.due_date),
    line_items: passedInvoice?.line_items || [],
    tax_rate_bps: Number(passedInvoice?.tax_rate_bps) || 0,
    status: passedInvoice?.status || 'OPEN',
    payment_date: passedInvoice?.payment_date || '',
    payment_notes: passedInvoice?.payment_notes || '',
  });

  const [form, setForm] = useState(createInitialInvoice());
  const [originalForm, setOriginalForm] = useState(createInitialInvoice());

  const formatDateForInput = (date) => {
    if (!date) return '';
    return formatDateInPhilippines(date) || '';
  };

  /* 
  client address/addresses object structure
  address: {
    "area":'',
    "zipcode":'',
    "house_unit_street": '',
    "city": '',
    "id":'', //not needed
    "barangay": '',
    "metro_province": '',
    "billing": true, //not needed
  }
  */
  const formatAddress = (addressObj) => {
    if (!addressObj) return "";
    const parts = [];
    if (addressObj.house_unit_street) parts.push(addressObj.house_unit_street);
    if (addressObj.barangay) parts.push(addressObj.barangay);
    if (addressObj.city) parts.push(addressObj.city);
    if (addressObj.metro_province) parts.push(addressObj.metro_province);
    if (addressObj.zipcode) parts.push(addressObj.zipcode);

    return parts.join(", ");
  }

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!contractId) return;
      try {
        const data = await getInvoicesByContractId(contractId);
        const items = data?.items ?? data?.invoices ?? (Array.isArray(data) ? data : []);
        const existingTotalMinor = items.reduce((sum, inv) => sum + (Number(inv.total_due_minor) || 0), 0);
        const expected = selectedContract?.payment_terms?.count ?? null;
        setInvoiceCountData({ actual: items.length, expected, existingTotalMinor });
      } catch {

      }
    };
    fetchInvoiceData();
  }, [contractId, selectedContract]);

  useEffect(() => {
    const hasFormChanges = JSON.stringify(form) !== JSON.stringify(originalForm);
    setHasChanges(hasFormChanges);
  }, [form, originalForm]);

  useEffect(() => {
    if (form.status === 'PAID') return;

    const autoStatus = getUnpaidInvoiceStatus(form.due_date);
    setForm((prev) =>
      prev.status === autoStatus ? prev : { ...prev, status: autoStatus }
    );
  }, [form.due_date, form.status]);

  const calculateTotals = () => {
    const netTotal = form.line_items.reduce((sum, item) => sum + Number(item.amount_minor || 0), 0);
    const taxAmount = Math.round(netTotal * (form.tax_rate_bps / 10000));
    const totalDue = netTotal + taxAmount;
    return { netTotal, taxAmount, totalDue };
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleStatusChange = (value) => {
    setForm((prev) => {
      if (value === 'PAID') {
        return {
          ...prev,
          status: 'PAID',
          payment_date: prev.payment_date || todayInPhilippines(),
        };
      }

      return {
        ...prev,
        status: getUnpaidInvoiceStatus(prev.due_date),
        payment_date: '',
        payment_notes: '',
      };
    });

    if (errors.status) {
      setErrors((prev) => ({
        ...prev,
        status: '',
      }));
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedLineItems = [...form.line_items];
    updatedLineItems[index] = {
      ...updatedLineItems[index],
      [field]: field === 'amount_minor' ? Number(value) : value,
    };
    setForm((prev) => ({ 
      ...prev,
      line_items: updatedLineItems,
    }));
  };

  const handleAmountInput = (index, e) => {
    const digits = e.target.value.replace(/\D/g, '');
    handleLineItemChange(index, 'amount_minor', parseInt(digits || '0', 10));
  }

  const handleTaxRateInput = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    handleFieldChange('tax_rate_bps', parseInt(digits || '0', 10));
  }

  const handleClientFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      client: {
        ...prev.client,
        [field]: value
      }
    }));
  };

  const removeLineItem = (index) => {
    const updatedLineItems = form.line_items.filter((_, i) => i !== index);
    setForm((prev) => ({
      ...prev,
      line_items: updatedLineItems,
    }));
  };

  const addLineItem = () => {
    const newId = String(Math.max(...form.line_items.map((item) => parseInt(item.id) || 0), 0) + 1);
    setForm((prev) => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          id: newId,
          sale_date: "",
          description: "",
          amount_minor: 0,
        },
      ],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrors({});

      if (form.contract_id) {
        try {
          const contractData = await getContractItem(selectedClient?.id);
          const contractExists = contractData.contracts?.some(
            (contract) => (contract.contract_id || contract.id) === form.contract_id
          );

          if (!contractExists) {
            setLoading(false);
            setErrors({ contract_id: 'Contract not found. Please select a valid contract.' });
            return;
          }
        } catch (contractError) {
          setLoading(false);
          console.error('Error validating contract.', contractError);
          setErrors({ contract_id: 'Unable to validate contract. Please try again' });
          return;
        }
      }

      const statusForSubmit = form.status === 'PAID'
        ? 'PAID'
        : getUnpaidInvoiceStatus(form.due_date);
      const formToValidate = {
        ...form,
        status: statusForSubmit,
      };

      await invoiceValidationSchema.validate(formToValidate, { abortEarly: false });

      const { netTotal, taxAmount, totalDue } = calculateTotals();
      const stored = localStorage.getItem('user');
      const storedUser = stored ? JSON.parse(stored) : null;
      const userInfo = storedUser
        ? {
            user_id: storedUser.username || storedUser.user_id || storedUser.user_ud || '',
            name: `${storedUser.firstname || storedUser.first_name || ''} ${storedUser.lastname || storedUser.last_name || ''}`.trim() || storedUser.name || storedUser.username || '',
          }
        : { user_id: 'system', name: 'system' };

      const submitData = {
        invoice_number: form.invoice_number,
        contract_id: form.contract_id,
        client: form.client,
        billing_date: formatDate(form.billing_date),
        due_date: formatDate(form.due_date),
        line_items: form.line_items,
        net_total_minor: netTotal,
        tax_rate_bps: form.tax_rate_bps,
        tax_minor: taxAmount,
        total_due_minor: totalDue,
        status: statusForSubmit,
        payment_date: form.payment_date,
        payment_notes: form.payment_notes,
        user_id: userInfo.user_id,
        user_name: userInfo.name,
      };

      const warnings = [];
      const contractPrice = selectedContract?.price_minor ?? null;
      if (contractPrice !== null && invoiceCountData?.existingTotalMinor !== undefined) {
        const originalTotalMinor = Number(passedInvoice?.total_due_minor) || 0;
        const projectedTotal = invoiceCountData.existingTotalMinor - originalTotalMinor + totalDue;
        if (projectedTotal > contractPrice) {
          const fmt = (v) => (v / 100).toFixed(2);
          warnings.push(
            `This invoice's new total (${fmt(totalDue)}) would bring all invoices under this contract to ${fmt(projectedTotal)}, exceeding the contract price of ${fmt(contractPrice)}.`
          );
        }
      }
      if (warnings.length > 0){
        setOverrideWarnings(warnings);
        setPendingSubmitData(submitData);
        setOverrideDialogOpen(true);
        setLoading(false);
        return;
      }

      const res = await patchInvoiceItem(
        passedInvoice?.id || passedInvoice?.invoice_id,
        submitData
      );

      setLoading(false);
      if (res) {
        setSuccessMessage('Invoice updated successfully!');
        setTimeout(() => {
          navigateAfterUpdate(res, submitData);
        }, 1500);
      }
    } catch (err) {
      setLoading(false);
      console.error('Validation error:', err);
      if (err.inner) {
        const map = {};
        err.inner.forEach((e) => {
          map[e.path] = e.message;
        });
        setErrors(map);
      } else {
        setErrors({ form: err.message || 'Update failed' });
      }
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setCancelDialogOpen(true);
    } else {
      navigate(backRoute, { state: backState });
    }
  };

  const handleConfirmCancel = () => {
    setCancelDialogOpen(false);
    navigate(backRoute, { state: backState });
  }

  const handleOverrideProceed = async () => {
    setOverrideDialogOpen(false);
    if (pendingSubmitData) {
      const res = await patchInvoiceItem(
        passedInvoice?.id || passedInvoice?.invoice_id,
        pendingSubmitData
      );
      setPendingSubmitData(null);
      setLoading(false);
      if (res) {
        setSuccessMessage('Invoice updated successfully!');
        setTimeout(() => {
          navigateAfterUpdate(res, pendingSubmitData);
        }, 1500);
      }
    }
  };

  const { netTotal, taxAmount, totalDue } = calculateTotals();

  if (!passedInvoice) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity='error'>Invoice not found</Alert>
        <Button variant='outlined' onClick={handleCancel} sx={{ mt: 2 }}>
          Back to Contracts
        </Button>
      </Box>
    );
  }

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title='Billing'
      subTitle='Edit Invoice'
      subTitleRoute={'/manage-contracts'}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
    >
      <Box component='form' onSubmit={handleSubmit} sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Invoice Details Headerr */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Invoice Details
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Grid>

          {/* Invoice Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Invoice Number"
              value={form.invoice_number}
              onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
              fullWidth
              disabled
              variant='outlined'
            />
          </Grid>

          {/* Contract ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contract ID"
              value={form.contract_id}
              onChange={(e) => handleFieldChange('contract_id', e.target.value)}
              fullWidth
              disabled
              error={!!errors.contract_id}
              variant='outlined'
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            {isSystemAdmin ? (
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  label='Status'
                >
                  <MenuItem value='OPEN'>Open</MenuItem>
                  <MenuItem value='PAID'>Paid</MenuItem>
                  <MenuItem value='OVERDUE'>Overdue</MenuItem>
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            ) : (
              <TextField
                label="Status"
                value={form.status}
                fullWidth
                disabled
                variant="outlined"
              />
            )}
          </Grid>

          {form.status === 'PAID' && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Payment Date"
                  type="date"
                  value={formatDateForInput(form.payment_date)}
                  onChange={(e) => handleFieldChange('payment_date', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Payment Notes"
                  value={form.payment_notes || 'N/A'}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled
                  variant="outlined"
                />
              </Grid>
            </>
          )}

          {/* Client Information */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Client Information
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>

          {/* Client Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client Name"
              value={form.client?.name || ""}
              onChange={(e) => handleClientFieldChange("name", e.target.value)}
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              error={!!errors["client.name"]}
              helperText={errors["client.name"]}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Client ID'
              value={form.client?.id || ''}
              fullWidth
              disabled
              variant='outlined'
            />
          </Grid>

          {/* Billing Address */}
          <Grid item xs={12}>
            <TextField
              label='Billing Address'
              value={form.client?.billing_address || ''}
              onChange={(e) => handleClientFieldChange('billing_address', e.target.value)}
              fullWidth
              multiline
              minRows={3}
              InputLabelProps={{ shrink: true }}
              error={!!errors['client.billing_address']}
              helperText={errors['client.billing_address']}
              disabled={clientLoading}
            />
          </Grid>

          {/* Dates */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Invoice Dates
                </Typography>
              }
              sx={{ pb: 0, pt: 0, mt: 2 }}
            />
          </Grid>

          {/* Billing Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Billing Date"
                value={form.billing_date}
                onChange={(date) => handleFieldChange("billing_date", date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.billing_date,
                    helperText: errors.billing_date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Due Date'
              type='date'
              value={formatDateForInput(form.due_date)}
              onChange={(e) => handleFieldChange('due_date', dateOnlyToDate(e.target.value))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.due_date}
              helperText={errors.due_date}
              variant='outlined'
            />
          </Grid>

          {/* Line Items Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Line Items
                </Typography>
              }
              sx={{ pb: 0, pt: 2, mt: 2 }}
            />
          </Grid>

          {/* Line Items Table */}
          {form.line_items.length > 0 && (
            <>
              <Grid item xs={12}>
                <TableContainer>
                  <Table
                    sx={{
                      borderCollapse: "separate",
                      borderSpacing: "0 12px",
                    }}
                  >
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ width: 180 }}>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right" sx={{ width: 190 }}>
                          Amount
                        </TableCell>
                        <TableCell align="center" sx={{ width: 90 }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {form.line_items.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            backgroundColor: "#fafafa",
                            "& td": {
                              py: 2,
                              px: 2,
                              verticalAlign: "top",
                              borderBottom: "1px solid #e5e7eb",
                            },
                            "& td:first-of-type": {
                              borderTopLeftRadius: 8,
                              borderBottomLeftRadius: 8,
                            },
                            "& td:last-of-type": {
                              borderTopRightRadius: 8,
                              borderBottomRightRadius: 8,
                            },
                          }}
                        >
                          <TableCell sx={{ width: 190 }}>
                            <Box sx={{ pt: "40px" }}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="Date"
                                  value={dateOnlyToDate(item.sale_date)}
                                  onChange={(date) =>
                                    handleLineItemChange(index, "sale_date", formatDate(date))
                                  }
                                  slotProps={{
                                    textField: {
                                      size: "small",
                                      fullWidth: true,
                                    },
                                  }}
                                />
                              </LocalizationProvider>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ minWidth: 420 }}>
                              <RichTextDescriptionEditor
                                value={item.description}
                                onChange={(html) =>
                                  handleLineItemChange(index, "description", html)
                                }
                              />

                              {errors[`line_items[${index}].description`] && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{ mt: 0.5, display: "block" }}
                                >
                                  {errors[`line_items[${index}].description`]}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell align="right" sx={{ width: 190 }}>
                            <Box sx={{ pt: "40px", display: "flex", justifyContent: "flex-end" }}>
                              <TextField
                                size="small"
                                type="text"
                                value={(item.amount_minor / 100).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                                onChange={(e) => handleAmountInput(index, e)}
                                inputProps={{ inputMode: "numeric" }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">{CURRENCY}</InputAdornment>
                                  ),
                                }}
                                error={!!errors[`line_items[${index}].amount_minor`]}
                                helperText={errors[`line_items[${index}].amount_minor`]}
                                variant="outlined"
                                sx={{ width: 170 }}
                              />
                            </Box>
                          </TableCell>

                          <TableCell align="center" sx={{ width: 80 }}>
                            <Box sx={{ pt: "40px" }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeLineItem(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </>
          )}
          {/* Add Line Item Button */}
          <Grid item xs={12}>
            <Button
              variant='outlined'
              size='small'
              onClick={addLineItem}
            >
              Add Line Item
            </Button>
          </Grid>

          {/* Tax Rate */}
          <Grid item xs={12} sm={6}>
            <TextField
              label='Tax Rate (bps)'
              type='text'
              value={(form.tax_rate_bps / 100).toFixed(2)}
              onChange={handleTaxRateInput}
              inputProps={{ inputMode: 'numeric' }}
              InputProps={{
                endAdornment: <InputAdornment position='end'>%</InputAdornment>
              }}
              error={!!errors.tax_rate_bps}
              helperText={errors.tax_rate_bps}
              variant='outlined'
            />
          </Grid>

          {/* Totatls Header */}
          <Grid item xs={12}>
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    paddingBottom: '0.5rem',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    fontSize: '1rem',
                  }}
                >
                  Totals
                </Typography>
              }
              sx={{ pb: 0, pt: 2, mt: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant='body2'> NetTotal:</Typography>
                  <Typography variant='h6'>
                    {CURRENCY} {(netTotal / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant='body2'>Tax Amount:</Typography>
                  <Typography variant='h6'>{CURRENCY} {(taxAmount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant='body2'>Total Due:</Typography>
                  <Typography vairant='h6' sx={{ fontWeight: 'bold' }}>{CURRENCY} {(totalDue / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
            <Button
              variant='contained'
              type='submit'
              disabled={!hasChanges || loading}
              sx={{ mr: 2 }}
            >
              {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
              {loading ? 'Updating...' : 'Update Invoice'}
            </Button>
            <Button
              variant='outlined'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Discard changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. If you leave now, your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Stay</Button>
          <Button onClick={handleConfirmCancel} color='error'>Discard</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)}>
        <DialogTitle>Contract Price Warning</DialogTitle>
        <DialogContent>
          {overrideWarnings.map((msg, i) => (
            <DialogContentText key={i} sx={{ mb: i < overrideWarnings.length - 1 ? 1.5 : 0 }}>
              {msg}
            </DialogContentText>
          ))}
          <DialogContentText sx={{ mt: 2 }}>
            Do you want to proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleOverrideProceed} variant='contained' color='warning'>
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </BaseWrapper>
  );
}

export default EditInvoice;
